import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Tag, FlaskConical, Download, Upload, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { StatusBadge } from '@/components/ui/status-badge';
import { FilterChips } from '@/components/ui/filter-chips';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { Combobox } from '@/components/ui/combobox';
import { useAuthStore } from '@/stores/auth.store';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Link } from 'react-router-dom';
import { TAX_RATES_PATH } from '@/lib/tax';
import { ProductsInSheet, type ProductsInTarget } from '@/components/catalog/ProductsInSheet';
import { ImportCsvDialog, downloadCsv } from '@/components/catalog/ImportCsvDialog';

interface CategoryTaxRate {
  id?: string;
  name: string;
  rate?: number | string;
  type?: string;
  isActive?: boolean;
  fbrSroScheduleNo?: string | null;
  fbrScenarioId?: string | null;
}

interface CategoryTaxLink {
  taxRateId: string;
  taxRate?: CategoryTaxRate | null;
}

interface CategoryRow {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  parentId?: string | null;
  imageUrl?: string | null;
  isActive?: boolean;
  sortOrder?: number | null;
  markupPercent?: number | string | null;
  fbrSroScheduleNo?: string | null;
  fbrScenarioId?: string | null;
  skipPaymentMethodTax?: boolean | null;
  taxes?: CategoryTaxLink[];
  _count?: { products?: number };
}

interface TaxRateRow extends CategoryTaxRate {
  id: string;
}

interface FbrSroSchedule {
  sroDesc: string;
}

interface CategoryPayload {
  name: string;
  slug?: string;
  description?: string;
  parentId?: string;
  imageUrl?: string | null;
  isActive: boolean;
  sortOrder: number;
  markupPercent?: number;
  taxRateIds: string[];
  fbrSroScheduleNo?: string;
  fbrScenarioId?: string;
  skipPaymentMethodTax: boolean;
}

interface PagedList<T> {
  data?: T[];
  meta?: { total?: number };
  total?: number;
}

function getEffectiveCategoryFbr(category: CategoryRow) {
  if (category.fbrSroScheduleNo || category.fbrScenarioId) {
    return { scheduleNo: category.fbrSroScheduleNo, scenarioId: category.fbrScenarioId, source: 'override' as const };
  }
  for (const t of (category.taxes ?? [])) {
    if (t.taxRate?.fbrSroScheduleNo || t.taxRate?.fbrScenarioId) {
      return { scheduleNo: t.taxRate.fbrSroScheduleNo, scenarioId: t.taxRate.fbrScenarioId, source: 'tax-rate' as const, taxRateName: t.taxRate.name };
    }
  }
  return null;
}

const defaultForm = {
  name: '',
  slug: '',
  description: '',
  parentId: '',
  imageUrl: '',
  isActive: true,
  sortOrder: '0',
  markupPercent: '',
  taxRateIds: [] as string[],
  fbrSroScheduleNo: '',
  fbrScenarioId: '',
  skipPaymentMethodTax: false,
};

type StatusFilter = 'active' | 'inactive' | 'all';
interface TreeRow { cat: CategoryRow; depth: number; childCount: number; }

/** POS-I372: children directly under their parent; a category whose parent is not in the list sits at the top. */
function buildTree(list: CategoryRow[], collapsed: Set<string>, forceOpen: boolean): TreeRow[] {
  const ids = new Set(list.map((c) => c.id));
  const kids = new Map<string, CategoryRow[]>();
  const roots: CategoryRow[] = [];
  for (const c of list) {
    if (c.parentId && ids.has(c.parentId) && c.parentId !== c.id) kids.set(c.parentId, [...(kids.get(c.parentId) ?? []), c]);
    else roots.push(c);
  }
  const out: TreeRow[] = [];
  const seen = new Set<string>();
  const walk = (c: CategoryRow, depth: number) => {
    if (seen.has(c.id) || depth > 8) return;
    seen.add(c.id);
    const children = kids.get(c.id) ?? [];
    out.push({ cat: c, depth, childCount: children.length });
    if (forceOpen || !collapsed.has(c.id)) children.forEach((k) => walk(k, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return out;
}

export function CategoriesPage() {
  const { t } = useTranslation('products');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('products:create') || hasPermission('products:*') || hasPermission('inventory:create') || hasPermission('inventory:*');
  const canUpdate = hasPermission('products:update') || hasPermission('products:*') || hasPermission('inventory:update') || hasPermission('inventory:*');
  const canDelete = hasPermission('products:delete') || hasPermission('products:*') || hasPermission('inventory:delete') || hasPermission('inventory:*');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read');
  const canOpenTaxRates = hasPermission('tax-rates:read') || hasPermission('tax-rates:*');

  const fbrScenarioOptions = [
    { label: t('fbrScenarioNone'), value: '' },
    { label: t('fbrScenarioSN001'), value: 'SN001' },
    { label: t('fbrScenarioSN002'), value: 'SN002' },
    { label: t('fbrScenarioSN005'), value: 'SN005' },
    { label: t('fbrScenarioSN006'), value: 'SN006' },
    { label: t('fbrScenarioSN007'), value: 'SN007' },
    { label: t('fbrScenarioSN008'), value: 'SN008' },
    { label: t('fbrScenarioSN016'), value: 'SN016' },
    { label: t('fbrScenarioSN017'), value: 'SN017' },
    { label: t('fbrScenarioSN024'), value: 'SN024' },
    { label: t('fbrScenarioSN026'), value: 'SN026' },
    { label: t('fbrScenarioSN027'), value: 'SN027' },
    { label: t('fbrScenarioSN028'), value: 'SN028' },
  ];

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const limit = 25;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryRow | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; products: number }>({ open: false, id: '', name: '', products: 0 });
  const [productsIn, setProductsIn] = useState<ProductsInTarget | null>(null);
  const [nameError, setNameError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setNameError('');
    if (dialogOpen && editCategory) {
      setForm({
        name: editCategory.name ?? '',
        slug: editCategory.slug ?? '',
        description: editCategory.description ?? '',
        parentId: editCategory.parentId ?? '',
        imageUrl: editCategory.imageUrl ?? '',
        isActive: editCategory.isActive !== false,
        sortOrder: editCategory.sortOrder != null ? String(editCategory.sortOrder) : '0',
        markupPercent: editCategory.markupPercent != null ? String(editCategory.markupPercent) : '',
        taxRateIds: editCategory.taxes?.map((tx) => tx.taxRateId) ?? [],
        fbrSroScheduleNo: editCategory.fbrSroScheduleNo ?? '',
        fbrScenarioId: editCategory.fbrScenarioId ?? '',
        skipPaymentMethodTax: editCategory.skipPaymentMethodTax ?? false,
      });
      setSlugManuallyEdited(true);
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
      setSlugManuallyEdited(false);
    }
  }, [dialogOpen, editCategory]);

  const { data: categories, isLoading } = useQuery<CategoryRow[] | PagedList<CategoryRow>>({
    queryKey: ['categories', 'admin-list'],
    queryFn: async () => {
      const res = await api.get('/categories', { params: { status: 'all' } });
      return res.data;
    },
  });

  const { data: taxRates } = useQuery<TaxRateRow[] | PagedList<TaxRateRow>>({
    queryKey: ['tax-rates'],
    queryFn: async () => { const res = await api.get('/tax-rates'); return res.data; },
  });

  const { data: fbrSroSchedules } = useQuery<FbrSroSchedule[]>({
    queryKey: ['fbr-reference-sro'],
    queryFn: async () => { const res = await api.get('/fbr/reference/sro-schedules'); return res.data; },
    staleTime: 1000 * 60 * 60,
    enabled: canViewFbr,
  });

  const sroOptions = [
    { label: t('sroNoneNotApplicable'), value: '' },
    ...(fbrSroSchedules ?? [])
      .map((s) => ({ label: s.sroDesc, value: s.sroDesc }))
      .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i)
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: CategoryPayload = {
        name: form.name,
        slug: form.slug || undefined,
        description: form.description || undefined,
        parentId: form.parentId || undefined,
        imageUrl: form.imageUrl.trim() || (editCategory ? null : undefined),
        isActive: form.isActive,
        sortOrder: form.sortOrder ? Number(form.sortOrder) : 0,
        markupPercent: form.markupPercent ? Number(form.markupPercent) : undefined,
        taxRateIds: form.taxRateIds,
        fbrSroScheduleNo: form.fbrSroScheduleNo || undefined,
        fbrScenarioId: form.fbrScenarioId || undefined,
        skipPaymentMethodTax: form.skipPaymentMethodTax,
      };
      if (editCategory) {
        const { data } = await api.patch(`/categories/${editCategory.id}`, payload);
        return data;
      }
      const { data } = await api.post('/categories', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editCategory ? t('categoryUpdated') : t('categoryCreated'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('saveFailed'));
      // POS-I370: a name clash is shown under the Name field.
      if (/already exists/i.test(message)) {
        setNameError(message);
        nameRef.current?.focus();
      }
      toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      toast.success(t('categoryDeleted'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, t('deleteFailed'))),
  });

  const categoriesList: CategoryRow[] = useMemo(() => (Array.isArray(categories) ? categories : categories?.data ?? []), [categories]);
  const needle = search.trim().toLowerCase();
  function deleteBlock(c: CategoryRow): { disabled?: boolean; disabledReason?: string } {
    const products = c._count?.products ?? 0;
    const subs = subCountById.get(c.id) ?? 0;
    if (products === 0 && subs === 0) return {};
    const what = [products > 0 ? t('productsInCount', { count: products }) : '', subs > 0 ? t('subCategoriesCount', { count: subs }) : ''].filter(Boolean).join(' + ');
    return { disabled: true, disabledReason: t('categoryDeleteBlocked', { what }) };
  }
  const nameById = useMemo(() => new Map(categoriesList.map((c) => [c.id, c.name])), [categoriesList]);
  /** POS-I378: active sub-categories per parent, so Delete can be disabled up front (the server refuses a category in use). */
  const subCountById = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of categoriesList) if (c.parentId && c.isActive !== false) m.set(c.parentId, (m.get(c.parentId) ?? 0) + 1);
    return m;
  }, [categoriesList]);
  const statusList = useMemo(
    () => categoriesList.filter((c) => (statusFilter === 'all' ? true : statusFilter === 'active' ? c.isActive !== false : c.isActive === false)),
    [categoriesList, statusFilter],
  );
  const filteredList = useMemo(
    () => (needle ? statusList.filter((c) => c.name.toLowerCase().includes(needle) || (c.slug ?? '').toLowerCase().includes(needle)) : statusList),
    [statusList, needle],
  );
  // A search shows a flat list so no match is hidden inside a collapsed parent.
  const treeRows = useMemo(
    () => (needle ? filteredList.map((cat) => ({ cat, depth: 0, childCount: 0 })) : buildTree(statusList, collapsed, false)),
    [filteredList, statusList, collapsed, needle],
  );
  const catTotal = treeRows.length;
  const pageRows = treeRows.slice((page - 1) * limit, page * limit);
  // POS-I362: the category and everything below it cannot be chosen as its parent.
  const blockedParentIds = useMemo(() => {
    const blocked = new Set<string>();
    if (!editCategory) return blocked;
    blocked.add(editCategory.id);
    let grew = true;
    while (grew) {
      grew = false;
      for (const c of categoriesList) {
        if (c.parentId && blocked.has(c.parentId) && !blocked.has(c.id)) { blocked.add(c.id); grew = true; }
      }
    }
    return blocked;
  }, [categoriesList, editCategory]);
  const taxRatesList: TaxRateRow[] = Array.isArray(taxRates) ? taxRates : taxRates?.data ?? [];

  function handleNameChange(name: string) {
    const updates: Partial<typeof form> = { name };
    if (!slugManuallyEdited) {
      updates.slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    setForm({ ...form, ...updates });
  }

  function handleSlugChange(slug: string) {
    setSlugManuallyEdited(true);
    setForm({ ...form, slug });
  }

  function toggleTaxRate(taxRateId: string) {
    setForm((prev) => ({
      ...prev,
      taxRateIds: prev.taxRateIds.includes(taxRateId)
        ? prev.taxRateIds.filter((id) => id !== taxRateId)
        : [...prev.taxRateIds, taxRateId],
    }));
  }

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('categoriesTitle')}
        subtitle={t('categoriesSubtitle')}
        action={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadCsv('/categories/export.csv', 'categories_' + new Date().toISOString().slice(0, 10) + '.csv').catch(() => toast.error('Export failed'))}>
              <Download className="me-2 h-4 w-4" />
              Export
            </Button>
            {canCreate && (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="me-2 h-4 w-4" />
                Import
              </Button>
            )}
            {canCreate && (
              <Button variant="gradient" onClick={() => { setEditCategory(null); setDialogOpen(true); }}>
                <Plus className="me-2 h-4 w-4" />
                {t('addCategory')}
              </Button>
            )}
          </div>
        )}
      />

      {isTestMode && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><strong>{t('testModeLabel')}</strong> {t('viewingTestCategories')} <a href="/settings" className="underline">{t('settingsLink')}</a> {t('toManageLiveCategories')}</span>
        </div>
      )}

      {/* Search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          placeholder={t('searchCategoriesPlaceholder')}
          value={search}
          onChange={(value) => { setSearch(value); setPage(1); }}
          className="max-w-sm"
        />
        <FilterChips
          chips={[
            { key: 'active', label: t('statusActive', 'Active') },
            { key: 'inactive', label: t('statusInactive', 'Inactive') },
            { key: 'all', label: t('statusAll', 'All') },
          ]}
          value={statusFilter}
          onChange={(key) => { setStatusFilter(key as StatusFilter); setPage(1); }}
          size="sm"
        />
      </div>

      {/* Categories table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('categoryName')}</Th>
                <Th>{t('slug')}</Th>
                <Th className="text-end">{t('productsColumn')}</Th>
                <Th>{t('appliedTaxes')}</Th>
                <Th>{t('fbrSettings')}</Th>
                <Th className="text-end">{t('actionsColumn')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="py-8">
                    <SkeletonTable rows={5} cols={6} />
                  </Td>
                </Tr>
              ) : statusList.length === 0 && categoriesList.length > 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="p-0">
                    <EmptyState
                      icon={Tag}
                      title={statusFilter === 'inactive' ? t('noInactiveCategories', 'No inactive categories') : t('noCategoriesFound')}
                      action={{ label: t('showAllCategories', 'Show all'), onClick: () => { setStatusFilter('all'); setPage(1); } }}
                    />
                  </Td>
                </Tr>
              ) : filteredList.length === 0 && categoriesList.length > 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="p-0">
                    <EmptyState
                      icon={Tag}
                      title={t('noCategoriesMatch', { search: search.trim() })}
                      action={{ label: t('clearSearch'), onClick: () => { setSearch(''); setPage(1); } }}
                    />
                  </Td>
                </Tr>
              ) : categoriesList.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="p-0">
                    <EmptyState
                      icon={Tag}
                      title={t('noCategoriesFound')}
                      description={t('addCategoryAction')}
                      action={canCreate ? { label: t('addCategory'), onClick: () => { setEditCategory(null); setDialogOpen(true); } } : undefined}
                    />
                  </Td>
                </Tr>
              ) : (
                pageRows.map(({ cat: category, depth, childCount }) => (
                  <Tr key={category.id}>
                    <Td className="font-medium td-name">
                      <div className="flex min-w-0 items-center gap-1.5" style={{ paddingInlineStart: depth * 20 }}>
                        {childCount > 0 ? (
                          <button
                            type="button"
                            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted"
                            aria-expanded={!collapsed.has(category.id)}
                            aria-label={(collapsed.has(category.id) ? t('expandCategory', 'Expand') : t('collapseCategory', 'Collapse')) + ' ' + category.name}
                            onClick={() => setCollapsed((prev) => { const n = new Set(prev); if (n.has(category.id)) n.delete(category.id); else n.add(category.id); return n; })}
                          >
                            {collapsed.has(category.id) ? <ChevronRight className="h-4 w-4 rtl:rotate-180" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        ) : (
                          <span className="inline-block w-5 shrink-0" aria-hidden />
                        )}
                        <span className="truncate" title={category.name}>{category.name}</span>
                        {childCount > 0 && (
                          <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground" title={t('subCategoriesCount', { count: childCount })}>
                            {childCount}
                          </span>
                        )}
                        {category.isActive === false && <StatusBadge status="INACTIVE" tone="warning" label={t('statusInactive', 'Inactive')} />}
                      </div>
                      {needle && category.parentId && nameById.get(category.parentId) && (
                        <div className="text-xs font-normal text-muted-foreground" style={{ paddingInlineStart: 26 }}>{t('inParent', 'in')} {nameById.get(category.parentId)}</div>
                      )}
                    </Td>
                    <Td label={t('slug')} className="text-muted-foreground td-hide">{category.slug || '—'}</Td>
                    <Td label={t('productsColumn')} className="text-end">
                      {(category._count?.products ?? 0) > 0 ? (
                        <button
                          type="button"
                          className="font-medium text-primary-text underline underline-offset-2 hover:no-underline"
                          onClick={() => setProductsIn({ kind: 'category', id: category.id, name: category.name })}
                        >
                          {category._count?.products}
                        </button>
                      ) : 0}
                    </Td>
                    <Td label={t('appliedTaxes')}>
                      {category.taxes && category.taxes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {category.taxes.map((tx) => (
                            <span
                              key={tx.taxRateId}
                              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text"
                            >
                              <Tag className="h-3 w-3" />
                              {tx.taxRate?.name?.includes('%') ? tx.taxRate.name : `${tx.taxRate?.name} ${Number(tx.taxRate?.rate)}%`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td label={t('fbrSettings')} className="text-xs td-hide">
                      {(() => {
                        const eff = getEffectiveCategoryFbr(category);
                        if (!eff) return <span className="text-muted-foreground">—</span>;
                        return (
                          <div className="space-y-1">
                            {eff.scheduleNo && (
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-xs border ${
                                eff.source === 'override'
                                  ? 'bg-warning/10 text-warning-text border-warning/30'
                                  : 'bg-muted text-muted-foreground border-border'
                              }`}>
                                {eff.scheduleNo}
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              {eff.scenarioId && (
                                <span className="text-muted-foreground">{eff.scenarioId}</span>
                              )}
                              {eff.source === 'override' && (
                                <span className="text-warning-text text-xs font-medium">⬆ {t('fbrOverrideLabel')}</span>
                              )}
                              {eff.source === 'tax-rate' && (
                                <span className="text-muted-foreground text-xs">{t('fbrFromTaxRate', { name: eff.taxRateName })}</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </Td>
                    <Td className="text-end td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('editCategory'),
                            icon: Edit,
                            onSelect: () => { setEditCategory(category); setDialogOpen(true); },
                            permission: canUpdate,
                            primary: true,
                          },
                          {
                            key: 'delete',
                            label: t('deleteCategory'),
                            icon: Trash2,
                            onSelect: () => setDeleteConfirm({ open: true, id: category.id, name: category.name, products: category._count?.products ?? 0 }),
                            permission: canDelete,
                            variant: 'destructive',
                            ...deleteBlock(category),
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {catTotal > limit && (
        <div className="border-t px-4 py-3 mt-1">
          <PaginationBar page={page} pageSize={limit} total={catTotal} onPageChange={setPage} />
        </div>
      )}

      {/* Add/Edit Category Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editCategory ? t('editCategory') : t('addCategory')}
        width="52rem"
        submitLabel={editCategory ? t('updateCategory') : t('createCategory')}
        isPending={saveMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); if (!form.name) return; saveMutation.mutate(); }}
        submitDisabled={!form.name}
      >
        <div className="space-y-4">
          <div>
            <Label>{t('categoryName')} *</Label>
            <Input
              value={form.name}
              ref={nameRef}
              onChange={(e) => { setNameError(''); handleNameChange(e.target.value); }}
              placeholder={t('categoryNamePlaceholder')}
              required
              aria-invalid={!!nameError}
            />
            {nameError && <p role="alert" className="mt-1 text-xs text-destructive-text">{nameError}</p>}
          </div>

          <div>
            <Label>{t('slug')}</Label>
            <Input
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder={t('slugPlaceholder')}
            />
          </div>

          <div>
            <Label>{t('description')}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('optionalDescription')}
              rows={3}
            />
          </div>

          <div>
            <Label>{t('parentCategory')}</Label>
            <Combobox
              options={categoriesList
                .filter((cat) => !blockedParentIds.has(cat.id) && (cat.isActive !== false || cat.id === form.parentId))
                .map((cat) => ({ value: cat.id, label: cat.name }))}
              value={form.parentId}
              onChange={(val) => setForm({ ...form, parentId: val })}
              placeholder={t('parentNone')}
              searchPlaceholder={t('searchCategoriesPlaceholder')}
              emptyText={t('noCategoriesFound')}
              allowClear
            />
          </div>

          <div>
            <Label>{t('imageUrl', 'Image URL')}</Label>
            <Input
              value={form.imageUrl}
              onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
              placeholder="https://"
              inputMode="url"
            />
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border border-input p-3">
            <div>
              <Label htmlFor="category-active" className="cursor-pointer">{t('statusActive', 'Active')}</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('categoryActiveHint', 'Inactive categories are hidden from the till and product pickers.')}</p>
            </div>
            <Switch id="category-active" checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} aria-label={t('statusActive', 'Active')} />
          </div>

          <div>
            <Label>{t('sortOrder')}</Label>
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              placeholder="0"
              min={0}
              step={1}
            />
          </div>

          <div>
            <Label>Markup %</Label>
            <Input
              type="number"
              value={form.markupPercent}
              onChange={(e) => setForm({ ...form, markupPercent: e.target.value })}
              placeholder="Default markup for products without their own"
              min={0}
              step={0.01}
            />
          </div>

          {/* Tax Rates multi-select */}
          <div>
            <Label>{t('taxRatesLabel')}</Label>
            <div className="mt-2 space-y-2 rounded-md border border-input p-3 max-h-40 overflow-y-auto">
              {taxRatesList.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('noTaxRates')}
                  {canOpenTaxRates && (
                    <>
                      {' · '}
                      <Link to={TAX_RATES_PATH} className="font-medium text-primary-text underline">{t('openTaxRatesPage')}</Link>
                    </>
                  )}
                </p>
              ) : (
                taxRatesList
                  .filter((tr) => tr.isActive !== false)
                  .map((tr) => (
                    <Label key={tr.id} className="flex items-center gap-2 cursor-pointer font-normal leading-normal text-foreground">
                      <Checkbox
                        checked={form.taxRateIds.includes(tr.id)}
                        onChange={() => toggleTaxRate(tr.id)}
                      />
                      <span className="text-sm">
                        {tr.name.includes('%') ? tr.name : `${tr.name} ${Number(tr.rate)}%`} – {tr.type}
                      </span>
                    </Label>
                  ))
              )}
            </div>
          </div>

          {/* FBR overrides — only shown when FBR addon is active */}
          {canViewFbr && (
          <div className="border rounded-lg p-3 space-y-3 bg-warning/10 border-warning/30">
            <p className="text-xs font-semibold text-warning-text uppercase tracking-wide">{t('fbrDiOverride')}</p>
            <p className="text-xs text-muted-foreground">{t('fbrOverrideDescription')}</p>

            {/* Show inherited from selected tax rates */}
            {form.taxRateIds.length > 0 && (() => {
              const inherited = taxRatesList.filter((tr) => form.taxRateIds.includes(tr.id) && (tr.fbrSroScheduleNo || tr.fbrScenarioId));
              if (inherited.length === 0) return null;
              return (
                <div className="rounded-md bg-muted border border-border p-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{t('inheritedFromTaxRates')}</p>
                  {inherited.map((tr) => (
                    <div key={tr.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium">{tr.name}:</span>
                      {tr.fbrSroScheduleNo && <span className="font-mono">{tr.fbrSroScheduleNo}</span>}
                      {tr.fbrScenarioId && <span className="text-muted-foreground">/ {tr.fbrScenarioId}</span>}
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">{t('selectOverridesHint')}</p>
                </div>
              );
            })()}

            <div>
              <Label className="text-xs">{t('fbrSroScheduleNo')}</Label>
              <Dropdown
                value={form.fbrSroScheduleNo}
                onChange={(e) => setForm({ ...form, fbrSroScheduleNo: e.value || '' })}
                options={sroOptions}
                placeholder={t('noneInheritFromTaxRate')}
                className="w-full"
                showClear
              />
            </div>

            <div>
              <Label className="text-xs">{t('fbrScenarioOverride')}</Label>
              <Dropdown
                value={form.fbrScenarioId}
                onChange={(e) => setForm({ ...form, fbrScenarioId: e.value || '' })}
                options={fbrScenarioOptions}
                placeholder={t('noneInheritFromTaxRate')}
                className="w-full"
                showClear
              />
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="skipPaymentMethodTax"
                checked={form.skipPaymentMethodTax}
                onChange={(e) => setForm({ ...form, skipPaymentMethodTax: e.target.checked })}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="skipPaymentMethodTax" className="cursor-pointer text-xs font-medium">
                  {t('skipIctServiceTax')}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('skipIctServiceTaxDesc')}
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </FormDialog>

      <ProductsInSheet target={productsIn} onClose={() => setProductsIn(null)} />
      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        noun="categories" nounOne="category"
        basePath="/categories"
        onImported={() => { queryClient.invalidateQueries({ queryKey: ['categories'] }); queryClient.invalidateQueries({ queryKey: ['products'] }); }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteCategory')}
        message={t('confirmDeleteCategory', { name: deleteConfirm.name }) + (deleteConfirm.products > 0 ? ' ' + t('categoryDeleteInUse', { count: deleteConfirm.products }) : '')}
        confirmLabel={t('deleteLabel')}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '', products: 0 }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '', products: 0 })}
      />
    </div>
  );
}
